/**
 * Mounts the live-call layer across the whole application.
 *
 * A caller can be looking at an order, a quotation or the HR module when the phone
 * rings, so the write-up has to be able to reach them anywhere — which means this sits
 * on the app shell rather than inside Sales Command Center.
 *
 * It used to run its own permission probe before mounting, and that was the bug: it
 * asked the ERP for a page record that does not exist in this deployment, fell through
 * to a session exchange of its own, and on any failure wrote a flag to sessionStorage
 * that disabled the feature *for the rest of the tab*. One stopped backend or one
 * network blip and the live call never appeared again until the tab was closed — with
 * the rest of the module working perfectly, because it had a session all along.
 *
 * So there is no separate probe now. The provider mounts, and the session it already
 * shares with every other Sales Command Center screen decides what happens:
 *
 *   no session      nothing polls (the provider gates on it) and nothing renders
 *   no live call    the dock renders nothing
 *   a live call     it appears, wherever the person happens to be
 *
 * Nothing is shown to somebody who cannot have a call, because a call they cannot have
 * does not exist. The permission question answers itself, and it answers it correctly
 * on every render rather than once, permanently, at startup.
 */
import React from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { sccQueryClient } from '../queryClient';
import LiveCallProvider from './LiveCallProvider';
import LiveCallDock from './LiveCallDock';
import '../live.css';

export default function LiveCallMount({ children }) {
  return (
    <QueryClientProvider client={sccQueryClient}>
      <LiveCallProvider>
        {children}
        <LiveCallDock />
      </LiveCallProvider>
    </QueryClientProvider>
  );
}
